import {toJS} from 'mobx';
import {User} from '../Auth/User';

export default class Utils {
    private static LoaderCache = new Map<string, any>();

    public static async Load(id: string, loader: () => Promise<any>) {
        if (User.Info.token === '') {
            return null;
        }

        if (Utils.LoaderCache.has(id)) {
            return Utils.LoaderCache.get(id);
        }

        const resp = loader();
        Utils.LoaderCache.set(id, resp);
        return await resp;
    }

    public static Clear(id: string) {
        Utils.LoaderCache.delete(id);
    }

    // Returns a function, that, as long as it continues to be invoked, will not
    // be triggered. The function will be called after it stops being called for
    // N milliseconds. If `immediate` is passed, trigger the function on the
    // leading edge, instead of the trailing.
    public static Debounce(func: () => void, wait: number, immediate: boolean = false) {
        var timeout: any;
        return function () {
            var later = function () {
                timeout = null;
                if (!immediate) {
                    func();
                }
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) {
                func();
            }
        };
    }

    public static IsNumeric(n: any) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    static MakeVarName(name: string): string {
        name = name.replace(/\W/g, '').replace(/\s\s+/g, ' ').trim().split(' ').join('_');

        if (/^\d/.test(name) && !/^\d+$/.test(name)) {
            name = '_' + name;
        }

        return name;
    }

    public static QuoteString(input: string): string {
        const s = input.indexOf("'") > -1;
        const q = input.indexOf('"') > -1;

        input = input.replaceAll('\n', '\\n');

        if (s && q) {
            return '"' + input.replaceAll('"', '\\"') + '"';
        } else if (q) {
            return "'" + input + "'";
        }

        return '"' + input + '"';
    }

    public static Str2Num(str: string): number {
        const parsed = parseFloat(str);
        if (isNaN(parsed)) {
            return 0;
        }
        return parsed;
    }

    public static Height2Inch(value: string): number {
        const args = value
            .replaceAll("'", ' ')
            .replaceAll('"', ' ')
            .split(' ')
            .filter((x) => x.trim() != '');

        let ft = 0;
        if (args.length > 0) {
            ft = Utils.Str2Num(args[0]);
        }

        let inch = 0;
        if (args.length > 1) {
            inch = Utils.Str2Num(args[1]);
        }
        return ft * 12 + inch;
    }

    public static Inch2Height(value: number): string {
        const ft = Math.floor(value / 12);
        const inch = value - ft * 12;
        return ft + "' " + inch + '"';
    }

    public static Date2Str(d: Date, addDays?: number) {
        if (addDays != undefined) {
            const dd = new Date();
            dd.setTime(d.getTime() + addDays * 24 * 3600000);
            d = dd;
        }
        var month = (d.getUTCMonth() + 1).toString();
        if (month.length == 1) {
            month = '0' + month;
        }

        var day = d.getUTCDate().toString();
        if (day.length == 1) {
            day = '0' + day;
        }

        return d.getUTCFullYear() + '-' + month + '-' + day;
    }

    public static Str2Date(s: string) {
        const args = s.split('-');
        return new Date(parseInt(args[0]), parseInt(args[1]) - 1, parseInt(args[2]), 0, 0, 0, 0);
    }

    public static jsClone<T>(o: T): T {
        return JSON.parse(JSON.stringify(toJS(o)));
    }

    public static async sleep(timeMs: number): Promise<void> {
        await new Promise((resolve) => {
            setTimeout(() => resolve(null), timeMs);
        });
    }

    public static isoString2TimeString(isoString: string): string {
        const date = new Date(isoString);

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
    }
}

export type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

export function truthy<T>(value: T): value is Truthy<T> {
    return !!value;
}
